import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { Agenda, AgendaItem, AgendaItemStatus, AgendaItemStatusChangedBy, AgendaItemSource } from '../../models/Agenda'
import { BaseResponse } from '../models'

const _BASE_URL = import.meta.env.REACT_APP_MEETING_SUPPORT_BASE_URL!

interface FindAgendaResponse {
  data: Agenda | Agenda[]
}

export interface GetAgendaRequest {
  accountId: string
  roomId: string
  sessionId: string
  meetingTemplateId: string
}

export interface CreateAgendaItemRequest {
  accountId: string
  agendaId: string
  item: {
    text: string
    orderingIndex: number
    source: AgendaItemSource
  }
}

export interface DeleteAgendaItemRequest {
  accountId: string
  agendaId: string
  agendaItemId: string
  roomId?: string
  sessionId?: string
  meetingTemplateId?: string
}

export interface UpdateAgendaItemRequest {
  accountId: string
  agendaId: string
  agendaItemId: string
  updatePayload: {
    id: string
    text?: string
    status?: AgendaItemStatus
    status_changed_by?: AgendaItemStatusChangedBy
    source?: AgendaItemSource
  }
}

interface UpdateAgendaItemResponse {
  data: AgendaItem
}

const transformResponse = <T>(response: BaseResponse<T>) => response.data

export const agendaApi = createApi({
  reducerPath: 'agendaApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Agenda'],
  endpoints: (builder) => ({
    getAgenda: builder.query<Agenda | null, GetAgendaRequest>({
      async queryFn({ accountId, roomId, sessionId, meetingTemplateId }, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `/accounts/${accountId}/agendas`,
          params: {
            type: 'meeting',
            room_id: roomId,
            session_id: sessionId,
            meeting_template_id: meetingTemplateId,
          },
        })

        if (result.error?.status === 404) {
          return { ...result, data: null, error: undefined }
        }

        if (result.error) {
          return { ...result, error: result.error }
        }

        const responseData = (result.data as FindAgendaResponse).data

        // Handle both single agenda and array response formats temporarily until we change the backend
        if (Array.isArray(responseData)) {
          if (responseData.length === 0) {
            return { ...result, data: null }
          }
          if (responseData.length === 1) {
            return { ...result, data: responseData[0] }
          }
          // Multiple agendas returned - this is an error condition, as we expect only one agenda per meeting and meeting template
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: `Multiple agendas found for roomId: ${roomId}, sessionId: ${sessionId}, meetingTemplateId: ${meetingTemplateId}`,
            } as FetchBaseQueryError,
          }
        }

        return { ...result, data: responseData }
      },
      providesTags: ['Agenda'],
    }),
    createAgendaItem: builder.mutation<AgendaItem, CreateAgendaItemRequest>({
      query: ({ accountId, agendaId, item }) => ({
        url: `/accounts/${accountId}/agendas/${agendaId}/items`,
        method: 'POST',
        body: item,
      }),
      transformResponse,
      invalidatesTags: ['Agenda'],
      async onQueryStarted({ agendaId, item }, { dispatch, queryFulfilled, getState }) {
        // Find all matching cache entries for this agenda
        const state = getState() as any
        const patches: any[] = []

        // Search through all getAgenda query cache entries
        Object.entries(state.agendaApi.queries).forEach(([key, value]: [string, any]) => {
          if (key.startsWith('getAgenda') && value?.data?.id === agendaId) {
            const queryArgs = value.originalArgs
            // Optimistically add the item to this cache entry
            const patchResult = dispatch(
              agendaApi.util.updateQueryData('getAgenda', queryArgs, (draft) => {
                if (draft) {
                  draft.items.push({
                    id: `temp-${Date.now()}`,
                    text: item.text,
                    orderingIndex: item.orderingIndex,
                    source: item.source,
                    status: AgendaItemStatus.Open,
                  })
                }
              }),
            )
            patches.push({ patchResult, queryArgs })
          }
        })

        try {
          const { data: createdItem } = await queryFulfilled
          // Replace temp item with real item from server in all affected cache entries
          patches.forEach(({ queryArgs }) => {
            dispatch(
              agendaApi.util.updateQueryData('getAgenda', queryArgs, (draft) => {
                if (draft) {
                  const tempIndex = draft.items.findIndex((i) => i.id.startsWith('temp-'))
                  if (tempIndex !== -1) {
                    draft.items[tempIndex] = createdItem
                  }
                }
              }),
            )
          })
        } catch {
          // Rollback all optimistic updates on error
          patches.forEach(({ patchResult }) => patchResult.undo())
        }
      },
    }),
    deleteAgendaItem: builder.mutation<void, DeleteAgendaItemRequest>({
      query: ({ accountId, agendaId, agendaItemId }) => ({
        url: `/accounts/${accountId}/agendas/${agendaId}/items/${agendaItemId}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ accountId, agendaItemId, roomId, sessionId, meetingTemplateId }, { dispatch, queryFulfilled }) {
        // Optimistically remove the item from cache
        const patchResult = dispatch(
          agendaApi.util.updateQueryData('getAgenda', { accountId, roomId: roomId!, sessionId: sessionId!, meetingTemplateId: meetingTemplateId! }, (draft) => {
            if (draft) {
              draft.items = draft.items.filter((item) => item.id !== agendaItemId)
            }
          }),
        )

        try {
          await queryFulfilled
        } catch {
          // Rollback optimistic update on error
          patchResult.undo()
        }
      },
    }),
    updateAgendaItem: builder.mutation<AgendaItem, UpdateAgendaItemRequest>({
      query: ({ accountId, agendaId, agendaItemId, updatePayload }) => ({
        url: `/accounts/${accountId}/agendas/${agendaId}/items/${agendaItemId}`,
        method: 'PATCH',
        body: updatePayload,
      }),
      transformResponse: (response: UpdateAgendaItemResponse) => response.data,
      async onQueryStarted({ agendaId, agendaItemId, updatePayload }, { dispatch, queryFulfilled, getState }) {
        // Find all matching cache entries for this agenda
        const state = getState() as any
        const patches: any[] = []

        // Search through all getAgenda query cache entries
        Object.entries(state.agendaApi.queries).forEach(([key, value]: [string, any]) => {
          if (key.startsWith('getAgenda') && value?.data?.id === agendaId) {
            const queryArgs = value.originalArgs
            // Optimistically update the item in this cache entry
            const patchResult = dispatch(
              agendaApi.util.updateQueryData('getAgenda', queryArgs, (draft) => {
                if (draft) {
                  const item = draft.items.find((i) => i.id === agendaItemId)
                  if (item) {
                    if (updatePayload.text !== undefined) item.text = updatePayload.text
                    if (updatePayload.status !== undefined) item.status = updatePayload.status
                    if (updatePayload.status_changed_by !== undefined) item.statusChangedBy = updatePayload.status_changed_by
                    if (updatePayload.source !== undefined) item.source = updatePayload.source
                  }
                }
              }),
            )
            patches.push(patchResult)
          }
        })

        try {
          await queryFulfilled
        } catch {
          // Rollback all optimistic updates on error
          patches.forEach((patchResult) => patchResult.undo())
        }
      },
    }),
  }),
})

export const { useGetAgendaQuery, useCreateAgendaItemMutation, useDeleteAgendaItemMutation, useUpdateAgendaItemMutation } = agendaApi
